import React, { useState, useCallback, useMemo } from 'react';
import { generateClient } from 'aws-amplify/api';
import { createCharacter } from '../graphql/mutations';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Sparkles, Book, Shield, Sword, Scroll,
  Heart, Star, User, RefreshCw, ChevronRight, 
  ChevronLeft, Dices, Info, AlertCircle, Feather
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './CharacterCreation.css';

// Import skill based character system
import { 
  INITIAL_CHARACTER_STATE, 
  CREATION_STEPS,
  calculateDerivedStats,
  calculateSpentSkillPoints,
  getSkillUpgradeCost,
  getSkillAttribute 
} from './character/creation/index';

// Import background implementation
import { BACKGROUNDS, applyBackgroundBonuses } from '../game-system/character/backgrounds';

// Import components for character creation steps
import { BasicInfo } from './character/creation/steps/BasicInfo';
import { AbilityScores } from './character/creation/steps/AbilityScores';
import { Background } from './character/creation/steps/Background';
import { Skills } from './character/creation/steps/Skills';

const NAME_GENERATION_QUERY = `
  query GenerateCharacterName($input: CharacterNameInput!) {
    generateCharacterName(input: $input) {
      name
    }
  }
`;

const BACKSTORY_GENERATION_QUERY = `
  query GenerateBackstory($input: BackstoryInput!) {
    generateBackstory(input: $input) {
      narrative
    }
  }
`;

const client = generateClient();

const SkillsBasedCharacterCreation = () => {
  const navigate = useNavigate();
  const { gameId } = useParams();
  
  // State Management
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [character, setCharacter] = useState(INITIAL_CHARACTER_STATE);

  // Memoized Character Update Function
  const updateCharacter = useCallback((updates) => {
    setCharacter(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // Handle attribute generation/rolling
  const rollAttributes = useCallback(async () => {
    setIsRolling(true);
    
    try {
      const newAttributes = {};
      for (const attribute of Object.keys(character.attributes)) {
        await new Promise(resolve => setTimeout(resolve, 200));
        const rolls = Array(4).fill(0).map(() => Math.floor(Math.random() * 6) + 1);
        rolls.sort((a, b) => b - a);
        newAttributes[attribute] = rolls.slice(0, 3).reduce((sum, roll) => sum + roll, 0);
      }

      // Calculate derived stats based on new attributes
      const derivedStats = calculateDerivedStats({
        ...character,
        attributes: newAttributes
      });

      updateCharacter({
        attributes: newAttributes,
        ...derivedStats
      });
      
      setSuccess('The dice have spoken!');
    } catch (err) {
      setError('The mystical dice failed to roll. Please try again.');
    } finally {
      setIsRolling(false);
    }
  }, [character, updateCharacter]);

  // Generate backstory using LLM
  const generateBackstory = useCallback(async () => {
    if (!character.name || !character.race || !character.alignment) {
      setError('Please complete your character details first');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      // Extract top skills for the backstory
      const topSkills = [];
      Object.entries(character.skills).forEach(([category, skills]) => {
        Object.entries(skills)
          .filter(([_skillName, rank]) => rank > 0)
          .sort(([_name1, rankA], [_name2, rankB]) => rankB - rankA)
          .slice(0, 2)
          .forEach(([skill, rank]) => {
            topSkills.push(`${skill} (${rank})`);
          });
      });

      const response = await client.graphql({
        query: BACKSTORY_GENERATION_QUERY,
        variables: {
          input: {
            characterName: character.name,
            characterRace: character.race,
            characterAlignment: character.alignment,
            characterBackground: character.background,
            characterAttributes: JSON.stringify(character.attributes),
            characterSkills: JSON.stringify(topSkills),
            personalityTraits: character.personalityTraits,
            ideals: character.ideals,
            bonds: character.bonds,
            flaws: character.flaws
          }
        }
      });

      updateCharacter({ backstory: response.data.generateBackstory.narrative });
      setSuccess('Your tale has been woven!');
    } catch (err) {
      console.error('Error generating backstory:', err);
      setError('The story weaving failed. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [character, updateCharacter]);

  // Navigation Handlers
  const handleStepChange = useCallback((index) => {
    // Add validation for each step
    const validateCurrentStep = () => {
      const step = CREATION_STEPS[currentStep];
      
      switch (step.id) {
        case 'basics':
          return character.name && character.race && character.alignment;
        case 'attributes':
          return Object.values(character.attributes).some(v => v !== 10);
        case 'skills':
          const spentPoints = calculateSpentSkillPoints(character.skills);
          return spentPoints > 0 && spentPoints <= character.skillPoints;
        case 'background':
          return character.background && character.backstory;
        default:
          return true;
      }
    };

    if (index < currentStep || validateCurrentStep()) {
      setCurrentStep(index);
      setError('');
    } else {
      setError('Please complete all required fields before proceeding');
    }
  }, [currentStep, character]);

  // Handle completion and character creation
  const handleComplete = useCallback(async () => {
    // Validate the current step
    const validateCurrentStep = () => {
      const step = CREATION_STEPS[currentStep];
      
      switch (step.id) {
        case 'basics':
          return character.name && character.race && character.alignment;
        case 'attributes':
          return Object.values(character.attributes).some(v => v !== 10);
        case 'skills':
          const spentPoints = calculateSpentSkillPoints(character.skills);
          return spentPoints > 0 && spentPoints <= character.skillPoints;
        case 'background':
          return character.background && character.backstory;
        default:
          return true;
      }
    };

    if (!validateCurrentStep()) {
      setError('Please complete all required fields before proceeding');
      return;
    }

    if (currentStep === CREATION_STEPS.length - 1) {
      try {
        setIsGenerating(true);

        // Calculate derived stats one final time
        const derivedStats = calculateDerivedStats(character);
        
        const characterInput = {
          name: character.name,
          // Store skills JSON string in the notes field for now
          // In a real implementation, you'd store these in a proper skill system
          characterClass: "Adventurer", // Generic class for all skill-based characters
          race: character.race,
          level: 1,
          experience: 0,
          maxHealth: derivedStats.maxHealth,
          currentHealth: derivedStats.maxHealth,
          gold: character.gold,
          strength: character.attributes.strength,
          dexterity: character.attributes.dexterity,
          constitution: character.attributes.constitution,
          intelligence: character.attributes.intelligence,
          wisdom: character.attributes.wisdom,
          charisma: character.attributes.charisma,
          armorClass: derivedStats.armorClass,
          speed: derivedStats.speed,
          initiative: derivedStats.initiative,
          proficiencyBonus: 2,
          alignment: character.alignment,
          backstory: character.backstory,
          notes: JSON.stringify({
            background: character.background,
            personality: {
              traits: character.personalityTraits,
              ideals: character.ideals,
              bonds: character.bonds,
              flaws: character.flaws
            },
            skills: character.skills
          })
        };

        console.log('Creating character with input:', characterInput);

        const response = await client.graphql({
          query: createCharacter,
          variables: {
            input: characterInput
          }
        });

        setSuccess('Your character has been forged!');
        setTimeout(() => {
          navigate(gameId ? `/games/${gameId}/play` : '/characters');
        }, 1500);
      } catch (err) {
        console.error('Error creating character:', err);
        if (err.message.includes('not authenticated')) {
          setError('Please sign in to create a character.');
        } else if (err.message.includes('validation')) {
          setError('Please check all character details are correct.');
        } else {
          setError(`Failed to forge your character. Please try again.`);
        }
      } finally {
        setIsGenerating(false);
      }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, character, navigate, gameId]);

  // Memoized UI Components - Step Content
  const stepContent = useMemo(() => {
    const currentStepConfig = CREATION_STEPS[currentStep];
    
    switch (currentStepConfig.id) {
      case 'basics':
        return (
          <BasicInfo 
            character={character} 
            onUpdate={updateCharacter} 
            onError={setError}
            onSuccess={setSuccess}
          />
        );
      case 'attributes':
        return (
          <AbilityScores 
            character={character} 
            onUpdate={updateCharacter} 
            onError={setError}
            onSuccess={setSuccess}
            onRoll={rollAttributes}
            isRolling={isRolling}
          />
        );
      case 'skills':
        return (
          <Skills 
            character={character} 
            onUpdate={updateCharacter} 
            onError={setError}
            onSuccess={setSuccess}
          />
        );
      case 'background':
        return (
          <Background 
            character={character} 
            onUpdate={updateCharacter} 
            onError={setError}
            onSuccess={setSuccess}
            onGenerateStory={generateBackstory}
            isGenerating={isGenerating}
          />
        );
      default:
        return null;
    }
  }, [currentStep, character, updateCharacter, rollAttributes, isRolling, generateBackstory, isGenerating]);

  // Character preview component
  const CharacterPreview = useMemo(() => {
    // Ensure character has a race set to prevent errors
    if (!character.race) {
      character.race = 'human'; // Default to human
    }

    // Calculate derived stats for preview
    const { maxHealth, armorClass, initiative } = calculateDerivedStats(character);

    // Get background name if available
    const backgroundName = character.background
      ? BACKGROUNDS[character.background]?.name
      : '';

    // Get top 3 skills to display
    const topSkills = [];
    Object.entries(character.skills).forEach(([category, skills]) => {
      Object.entries(skills)
        .filter(([_skillName, rank]) => rank > 0)
        .sort(([_name1, rankA], [_name2, rankB]) => rankB - rankA)
        .slice(0, 2)
        .forEach(([skill, rank]) => {
          const formattedSkill = skill
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase());
          topSkills.push(`${formattedSkill} (${rank})`);
        });
    });
    
    // Only show top 3 skills
    const displaySkills = topSkills.slice(0, 3);

    return (
      <div className="character-creation__preview">
        <div className="character-creation__preview-portrait">
          <motion.div
            className="character-creation__preview-frame"
            animate={{
              boxShadow: [
                "0 0 10px var(--color-rune-glow)",
                "0 0 20px var(--color-rune-glow)",
                "0 0 10px var(--color-rune-glow)"
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <User size={64} />
          </motion.div>
        </div>

        <div className="character-creation__preview-content">
          <h2>{character.name || "Unnamed Hero"}</h2>
          <p className="character-creation__preview-subtitle">
            {[character.race, backgroundName, character.alignment]
              .filter(Boolean)
              .join(" • ")}
          </p>

          <div className="character-creation__preview-stats">
            <div className="character-creation__stat">
              <Heart className="character-creation__stat-icon" />
              <div className="character-creation__stat-value">{maxHealth}</div>
              <div className="character-creation__stat-label">Health</div>
            </div>
            <div className="character-creation__stat">
              <Shield className="character-creation__stat-icon" />
              <div className="character-creation__stat-value">{armorClass}</div>
              <div className="character-creation__stat-label">Armor</div>
            </div>
            <div className="character-creation__stat">
              <Sword className="character-creation__stat-icon" />
              <div className="character-creation__stat-value">
                {initiative >= 0 ? `+${initiative}` : initiative}
              </div>
              <div className="character-creation__stat-label">Initiative</div>
            </div>
          </div>

          {displaySkills.length > 0 && (
            <div className="character-creation__preview-skills">
              <h4>Top Skills</h4>
              <ul>
                {displaySkills.map((skill, index) => (
                  <li key={index}>{skill}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }, [character]);

  // Message display component
  const MessageDisplay = useMemo(() => {
    if (!error && !success) return null;
    
    return (
      <>
        {error && (
          <motion.div
            className="character-creation__message character-creation__message--error"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <AlertCircle size={20} />
            <span>{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            className="character-creation__message character-creation__message--success"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Star size={20} />
            <span>{success}</span>
          </motion.div>
        )}
      </>
    );
  }, [error, success]);

  return (
    <div className="character-creation">
      {/* Header */}
      <header className="character-creation__header">
        <motion.h1
          className="character-creation__title"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Forge Your Legend
        </motion.h1>
        <p className="character-creation__subtitle">
          Create a character to begin your adventure
        </p>
      </header>
  
      {/* Steps Navigation */}
      <nav className="character-creation__steps">
        {CREATION_STEPS.map((step, index) => (
          <motion.div
            key={step.id}
            className={`character-creation__step
              ${currentStep === index ? 'character-creation__step--active' : ''}
              ${index < currentStep ? 'character-creation__step--completed' : ''}`}
            onClick={() => handleStepChange(index)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2 }}
          >
            <div className="character-creation__step-icon">
              {/* Use default icons based on step ID */}
              {step.id === 'basics' && <User size={28} />}
              {step.id === 'attributes' && <Star size={28} />}
              {step.id === 'skills' && <Sword size={28} />}
              {step.id === 'background' && <Book size={28} />}
              {step.id === 'equipment' && <Shield size={28} />}
              {step.id === 'review' && <RefreshCw size={28} />}
            </div>
            <div className="character-creation__step-title">
              {step.label}
            </div>
          </motion.div>
        ))}
      </nav>
  
      {/* Messages */}
      {MessageDisplay}
  
      {/* Steps Content */}
      <AnimatePresence mode="wait">
        {stepContent}
      </AnimatePresence>
  
      {/* Character Preview */}
      {CharacterPreview}
  
      {/* Controls */}
      <div className="character-creation__controls">
        {currentStep > 0 && (
          <button
            className="character-creation__button character-creation__button--secondary"
            onClick={() => setCurrentStep(prev => prev - 1)}
          >
            <ChevronLeft />
            Previous Step
          </button>
        )}
  
        <button
          className="character-creation__button"
          onClick={handleComplete}
        >
          {currentStep === CREATION_STEPS.length - 1 ? (
            <>
              <Star />
              Forge Character
            </>
          ) : (
            <>
              Next Step
              <ChevronRight />
            </>
          )}
        </button>
      </div>
  
      <div className="character-creation__magical-effect character-creation__magical-effect--top" />
      <div className="character-creation__magical-effect character-creation__magical-effect--bottom" />
    </div>
  );
};

export default SkillsBasedCharacterCreation;